import React, { Fragment, useEffect, useRef, useState } from 'react';

// Component

import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// Utils

import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import validators from '@utils/validators/validators';

// Constants

import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

// Auto-dismiss duration kept in sync with MaskedInput
const WARNING_AUTO_HIDE_MS = 10 * 1000;

const SocialSecurityNumberInput = props => {
    const {
        inputData: {
            id,
            isInputRequired,
            helperClasses,
            name,
            placeholder,
            type,
            value,
            valuesToMatch,
            validationType,
        },
        inputActions: { handleFieldChange },
        label: { text, labelClasses, ariaLabel },
        errorClasses,
        isErrorShown
    } = props;

    const [localState, setLocalState] = useState({
        currentSsn: value,
        isHidden: true,
    });
    const WARNING_TYPE = {
        UNIQUE: 'UNIQUE',
        INCORRECT: 'INCORRECT',
    };

    const warningsConfig = {
        [WARNING_TYPE.UNIQUE]: {
            visibility: false,
            warningText: VALIDATION_ERROR_MESSAGES.NOT_UNIQUE_SSN,
        },
        [WARNING_TYPE.INCORRECT]: {
            visibility: false,
            warningText: VALIDATION_ERROR_MESSAGES.INCORRECT_SSN,
        },
    };

    const [warnings, updateWarnings] = useState(warningsConfig);
    const timeoutRef = useRef(null);

    const { currentSsn, isHidden } = localState;

    useEffect(() => {
        if (isHidden) {
            setLocalState(state => ({
                ...state,
                currentSsn: value,
            }));
        }
    }, [value, isHidden]);

    // Suppress the inline warning while the form-level error is being shown.
    useEffect(() => {
        if (isErrorShown) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            updateWarnings(warningsConfig);
        }
    }, [isErrorShown]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const showMessage = type => {
        const toggleWarning = visibility =>
            updateWarnings(warnings => ({
                ...warnings,
                [type]: {
                    ...warnings[type],
                    visibility,
                },
            }));

        if (!isErrorShown) {
            toggleWarning(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                toggleWarning(false);
                timeoutRef.current = null;
            }, WARNING_AUTO_HIDE_MS);
        }
    };

    const handleSsnFocus = () => {
        setLocalState({
            currentSsn: socialSecurityNumberTools.getOnFocus(currentSsn),
            isHidden: false,
        });
    };

    const handleSsnChange = e => {
        const { value: nextSsnValue } = e.target;

        // Blocks when a non-digit (including '-') is being added at the end of the value
        // or replaces the entire content. Eg: "A", "-", "@", "123-4$", "444--"
        if ((nextSsnValue.length > currentSsn?.length || nextSsnValue.length === 1) && isNaN(+nextSsnValue.slice(-1))) {
            return null;
        }

        // Blocks paste/replace operations that contain any non-digit, non-dash chars
        // (e.g. "ABC-12-DEFG", " 3-45-6789", "123-45 "),
        // or if the value exceeds the formatted SSN length (XXX-XX-XXXX = 11 chars) 
        if (!/^[\d-]*$/.test(nextSsnValue) || nextSsnValue.length > 11) {
            return null;
        }

        const ssnNumberToSave = socialSecurityNumberTools.autoFillSsn(nextSsnValue, currentSsn ?? '');

        setLocalState(state => ({
            ...state,
            currentSsn: ssnNumberToSave,
        }));
    };

    const handleSsnBlur = () => {
        const cleanedSsn = socialSecurityNumberTools.cleanedUpSsn(currentSsn);
        const hasValue = !!cleanedSsn;

        let isSsnNumberValid = false;
        if (validators && validators.find(validator => validator.validatorType === VALIDATION_TYPES[validationType])) {
            isSsnNumberValid = validators
                .find(validator => validator.validatorType === VALIDATION_TYPES[validationType])
                .verifier({ value: cleanedSsn });
        } else {
            isSsnNumberValid = true;
        }

        // SSN is optional, but any entered value must validate; empty stays silent.
        if (!isSsnNumberValid && hasValue) {
            showMessage(WARNING_TYPE.INCORRECT);
        }

        const isSsnUnique = validators
            .find(validator => validator.validatorType === VALIDATION_TYPES.UNIQUE)
            .verifier({ value: cleanedSsn, valuesToMatch: valuesToMatch });

        if (!isSsnUnique && hasValue) {
            showMessage(WARNING_TYPE.UNIQUE);
        }

        // Clear any pending warning timer once the value becomes valid and unique.
        if (isSsnNumberValid && isSsnUnique && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            updateWarnings(warningsConfig);
        }

        handleFieldChange(isSsnNumberValid && isSsnUnique ? cleanedSsn : null);

        setLocalState(state => ({
            ...state,
            isHidden: true,
        }));
    };

    const getWarnings = () => {
        return Object.keys(warningsConfig).map(type => {
            return (
                <ValidationErrorMessage
                    key={type}
                    isVisible={warnings[type].visibility}
                    messageText={warnings[type].warningText}
                    errorClasses={errorClasses}
                />
            );
        });
    };

    return (
        <Fragment>
            {!isErrorShown && getWarnings()}
            <div className={`grid-row gi-component gi-${type} ${helperClasses && helperClasses.join(' ')}`}>
                <label
                    htmlFor={id}
                    data-testid={`${id}_label`}
                    className={`tablet:grid-col-4 gi-${type}__label ${
                        labelClasses && labelClasses.join(' ')
                    } required-${isInputRequired}`}
                    aria-label={ariaLabel}
                >
                    {text}
                </label>
                <input
                    id={id}
                    className={`tablet:grid-col-6 gi-${type}__input`}
                    data-testid={`${id}_input`}
                    name={name}
                    type="text"
                    value={
                        isHidden
                            ? socialSecurityNumberTools.maskSsn(currentSsn)
                            : socialSecurityNumberTools.getAdjustedSsn(currentSsn)
                    }
                    aria-required={isInputRequired}
                    placeholder={placeholder}
                    onChange={handleSsnChange}
                    onBlur={handleSsnBlur}
                    onFocus={handleSsnFocus}
                />
            </div>
        </Fragment>
    );
};

export default SocialSecurityNumberInput;
